import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Settings, BookOpen, FileText, Plus, Edit, Trash2, Save, X,
  Users, TrendingUp, BarChart3, CheckCircle, Clock, Award, ToggleLeft, ToggleRight
} from 'lucide-react';
import { designSystem } from '../designSystem';
import RichTextToolbar from '../components/RichTextToolbar';
import RichTextEditor from '../components/RichTextEditor';
import EditModeIndicator from '../components/EditModeIndicator';
import SystemAdminLayout from '../layouts/SystemAdminLayout';
import QuizReviewQueue from '../components/admin/QuizReviewQueue';

// Utility function to clean Microsoft Office XML markup and extract clean HTML
const cleanMicrosoftHTML = (htmlContent) => {
  if (!htmlContent) return '<p>No description available</p>';
  
  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  // Remove all MS Office conditional comments
  let cleaned = htmlContent.replace(/<!--\[if[^\]]*\]>[\s\S]*?<!\[endif\]-->/gi, '');
  
  // Remove XML declarations
  cleaned = cleaned.replace(/<\?xml[^>]*\?>/gi, '');
  
  // Remove all MS Office namespace declarations from tags
  cleaned = cleaned.replace(/\s+xmlns:[^=]*="[^"]*"/gi, '');
  cleaned = cleaned.replace(/\s+xmlns="[^"]*"/gi, '');
  
  // Remove style tags
  cleaned = cleaned.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  
  // Remove head tag and its contents
  cleaned = cleaned.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '');
  
  // Extract body content if present
  const bodyMatch = cleaned.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  if (bodyMatch) {
    cleaned = bodyMatch[1];
  }
  
  // Now clean up MS Office specific tags while preserving content
  // Remove o: prefixed tags but keep their content
  cleaned = cleaned.replace(/<o:p>([\s\S]*?)<\/o:p>/gi, '$1');
  cleaned = cleaned.replace(/<\/?o:[^>]*>/gi, '');
  
  // Remove w: and m: prefixed tags
  cleaned = cleaned.replace(/<\/?w:[^>]*>/gi, '');
  cleaned = cleaned.replace(/<\/?m:[^>]*>/gi, '');
  
  // Remove v: prefixed tags (VML)
  cleaned = cleaned.replace(/<\/?v:[^>]*>/gi, '');
  
  // Remove html tag
  cleaned = cleaned.replace(/<\/?html[^>]*>/gi, '');
  
  // Clean up class and style attributes that are MS Office specific
  cleaned = cleaned.replace(/\s+class="Mso[^"]*"/gi, '');
  cleaned = cleaned.replace(/\s+class="[^"]*Mso[^"]*"/gi, '');
  
  // Remove empty spans and divs
  cleaned = cleaned.replace(/<span[^>]*>\s*<\/span>/gi, '');
  cleaned = cleaned.replace(/<div[^>]*>\s*<\/div>/gi, '');
  
  // Clean up excessive whitespace
  cleaned = cleaned.replace(/\s+/g, ' ');
  cleaned = cleaned.replace(/>\s+</g, '><');
  
  // Remove empty paragraphs
  cleaned = cleaned.replace(/<p[^>]*>\s*(&nbsp;|\s)*<\/p>/gi, '');
  
  // Final check - if we have no meaningful content, return default
  tempDiv.innerHTML = cleaned;
  const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
  
  if (textContent.length === 0 || textContent.length < 10) {
    return '<p>No description available. Please add content.</p>';
  }
  
  return cleaned.trim();
};

const SystemAdminDashboard = () => {
  const location = useLocation();
  const initialView = location.state?.activeView || 'dashboard';
  const [activeView, setActiveView] = useState(initialView); // dashboard, cms, orgProgress
  const [pillars, setPillars] = useState([]);
  const [selectedPillar, setSelectedPillar] = useState(null);
  const [orgProgress, setOrgProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [cmsTab, setCmsTab] = useState('overview'); // CMS tab state
  
  // Modal states
  const [showSubPillarModal, setShowSubPillarModal] = useState(false);
  const [showAddPillarModal, setShowAddPillarModal] = useState(false);
  const [showEditPillarModal, setShowEditPillarModal] = useState(false);
  const [editingPillar, setEditingPillar] = useState(null);
  const [showEditModuleModal, setShowEditModuleModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [selectedSubPillar, setSelectedSubPillar] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [showPillarPreview, setShowPillarPreview] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchData();
  }, []);

  // Update activeView when location state changes
  useEffect(() => {
    if (location.state?.activeView) {
      setActiveView(location.state.activeView);
    }
  }, [location.state]);

  const fetchData = async () => {
    try {
      const [pillarsRes, progressRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/pillars/admin/all`),
        fetch(`${BACKEND_URL}/api/pillars/admin/organization-progress`)
      ]);
      
      const pillarsData = await pillarsRes.json();
      const progressData = await progressRes.json();
      
      setPillars(pillarsData);
      setOrgProgress(progressData);
      if (pillarsData.length > 0) {
        setSelectedPillar(pillarsData[0]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleUpdatePillar = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pillars/admin/pillar/${selectedPillar.pillar_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });
      if (response.ok) {
        setEditMode(false);
        // CRITICAL FIX: AWAIT fetchData() to ensure UI refreshes before showing alert
        await fetchData();
        alert('Pillar updated successfully!');
      } else {
        alert('Failed to update pillar - server error');
      }
    } catch (error) {
      console.error('Error updating pillar:', error);
      alert('Failed to update pillar');
    }
  };

  const handlePillarClick = (pillar) => {
    setSelectedPillar(pillar);
    setShowPillarPreview(true);
  };

  const handleTogglePillarStatus = async (pillar) => {
    const newStatus = pillar.status === 'published' ? 'draft' : 'published';
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/pillars/admin/pillar/${pillar.pillar_id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        fetchData();
        alert(`Pillar ${newStatus === 'published' ? 'published' : 'set to draft'}!`);
      }
    } catch (error) {
      console.error('Error toggling pillar status:', error);
      alert('Failed to update pillar status');
    }
  };

  const handleToggleModuleStatus = async (pillarId, moduleId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/pillars/admin/subpillar/${pillarId}/${moduleId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        fetchData();
        alert(`Module ${newStatus === 'active' ? 'activated' : 'deactivated'}!`);
      }
    } catch (error) {
      console.error('Error toggling module status:', error);
      alert('Failed to update module status');
    }
  };

  const handleEditModule = (pillar, module) => {
    setSelectedPillar(pillar);
    setEditingModule(module);
    setShowEditModuleModal(true);
  };

  const handleAddSubPillar = async (subPillarData) => {
    try {
      await fetch(`${BACKEND_URL}/api/pillars/admin/subpillar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pillar_id: selectedPillar.pillar_id,
          ...subPillarData
        })
      });
      setShowSubPillarModal(false);
      fetchData();
      alert('Sub-pillar added successfully!');
    } catch (error) {
      console.error('Error adding sub-pillar:', error);
      alert('Failed to add sub-pillar');
    }
  };

  const handleDeleteSubPillar = async (subPillarId) => {
    if (!confirm('Are you sure you want to delete this module?')) return;
    
    try {
      await fetch(`${BACKEND_URL}/api/pillars/admin/subpillar/${selectedPillar.pillar_id}/${subPillarId}`, {
        method: 'DELETE'
      });
      fetchData();
      alert('Sub-pillar deleted successfully!');
    } catch (error) {
      console.error('Error deleting sub-pillar:', error);
      alert('Failed to delete sub-pillar');
    }
  };

  const handleAddQuestion = async (questionData) => {
    try {
      await fetch(`${BACKEND_URL}/api/pillars/admin/question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pillar_id: selectedPillar.pillar_id,
          sub_pillar_id: selectedSubPillar.sub_pillar_id,
          ...questionData
        })
      });
      setShowQuestionModal(false);
      fetchData();
      alert('Question added successfully!');
    } catch (error) {
      console.error('Error adding question:', error);
      alert('Failed to add question');
    }
  };

  const handlePublish = async (pillarId) => {
    try {
      await fetch(`${BACKEND_URL}/api/pillars/admin/publish/${pillarId}`, {
        method: 'POST'
      });
      fetchData();
      alert('Pillar published successfully!');
    } catch (error) {
      console.error('Error publishing pillar:', error);
      alert('Failed to publish pillar');
    }
  };

  const handleSaveModule = async (updatedModule) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/pillars/admin/subpillar/${selectedPillar.pillar_id}/${encodeURIComponent(updatedModule.sub_pillar_id)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedModule)
        }
      );
      if (response.ok) {
        await fetchData();
        setShowEditModuleModal(false);
        setEditingModule(null);
        alert('Module updated successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to save: ${errorData.detail || 'Unknown error'}. Please refresh and try again.`);
      }
    } catch (error) {
      alert(`Failed to update module: ${error.message}`);
    }
  };

  const editModuleModal = showEditModuleModal && editingModule && selectedPillar ? (
    <EditModuleModal
      onClose={() => {
        setShowEditModuleModal(false);
        setEditingModule(null);
      }}
      pillar={selectedPillar}
      module={editingModule}
      onSave={handleSaveModule}
    />
  ) : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading System Admin CMS...</p>
        </div>
      </div>
    );
  }

  // Dashboard View
  if (activeView === 'dashboard') {
    return (
      <SystemAdminLayout>
        <div className="bg-gray-50 min-h-full">
        {/* Page Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-8 px-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-4xl font-extrabold mb-2" style={{ fontFamily: designSystem.typography.fonts.display }}>System Admin Dashboard</h1>
            <p className="text-lg text-indigo-100" style={{ fontSize: designSystem.typography.sizes.lg }}>6 Pillars Learning Management System</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-8 py-8">
          {/* Navigation Tabs */}
          <div className="bg-white rounded-2xl shadow-xl mb-10 p-3 flex space-x-3 border border-gray-100">
            <button
              onClick={() => setActiveView('dashboard')}
              className="flex-1 px-8 py-4 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold shadow-lg transform transition-all duration-200 hover:scale-105"
              style={{ fontSize: designSystem.typography.sizes.lg, fontFamily: designSystem.typography.fonts.primary }}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveView('cms')}
              className="flex-1 px-8 py-4 rounded-xl hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 font-bold text-gray-700 transition-all duration-200 hover:shadow-md"
              style={{ fontSize: designSystem.typography.sizes.lg, fontFamily: designSystem.typography.fonts.primary }}
            >
              Content Management
            </button>
            <button
              onClick={() => setActiveView('orgProgress')}
              className="flex-1 px-8 py-4 rounded-xl hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 font-bold text-gray-700 transition-all duration-200 hover:shadow-md"
              style={{ fontSize: designSystem.typography.sizes.lg, fontFamily: designSystem.typography.fonts.primary }}
            >
              Organization Progress
            </button>
          </div>

          {/* Stats Cards - Compact Version */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 border border-blue-100">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-blue-500 rounded-lg p-3 shadow-md">
                  <BookOpen className="h-7 w-7 text-white" />
                </div>
                <span data-testid="admin-active-pillar-count" className="text-3xl font-extrabold text-gray-900" style={{ fontFamily: designSystem.typography.fonts.display }}>{pillars.filter(p => p.is_active !== false).length}</span>
              </div>
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Active Pillars</h3>
              <p className="text-xs text-gray-500 mt-1">{pillars.filter(p => p.is_active === false).length} inactive (excluded from CIS)</p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 border border-green-100">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-green-500 rounded-lg p-3 shadow-md">
                  <FileText className="h-7 w-7 text-white" />
                </div>
                <span className="text-3xl font-extrabold text-gray-900" style={{ fontFamily: designSystem.typography.fonts.display }}>
                  {pillars.filter(p => p.is_active !== false).reduce((sum, p) => sum + (p.sub_pillars?.length || 0), 0)}
                </span>
              </div>
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Total Modules</h3>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 border border-purple-100">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-purple-500 rounded-lg p-3 shadow-md">
                  <Users className="h-7 w-7 text-white" />
                </div>
                <span className="text-3xl font-extrabold text-gray-900" style={{ fontFamily: designSystem.typography.fonts.display }}>{orgProgress.length}</span>
              </div>
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Organizations</h3>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 border border-amber-100">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-amber-500 rounded-lg p-3 shadow-md">
                  <TrendingUp className="h-7 w-7 text-white" />
                </div>
                <span className="text-3xl font-extrabold text-gray-900" style={{ fontFamily: designSystem.typography.fonts.display }}>
                  {orgProgress.length > 0 
                    ? Math.round(orgProgress.reduce((sum, org) => sum + (org.avg_completion || 0), 0) / orgProgress.length)
                    : 0}%
                </span>
              </div>
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Avg Completion</h3>
            </div>
          </div>

          {/* Quick Access - Assessment Engine */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl shadow-2xl p-8 mb-8 text-white hover:shadow-3xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                    <BarChart3 className="h-8 w-8" />
                  </div>
                  <h2 className="text-3xl font-extrabold" style={{ fontFamily: designSystem.typography.fonts.display }}>
                    Assessment Engine
                  </h2>
                </div>
                <p className="text-emerald-50 text-lg mb-4">
                  Manage the comprehensive assessment system with 60 questions across 6 pillars. Create assessments, review rubrics, and track organizational evaluations.
                </p>
                <div className="flex gap-4 text-sm">
                  <span className="bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm font-semibold">60 Questions</span>
                  <span className="bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm font-semibold">6 Pillars</span>
                  <span className="bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm font-semibold">Multi-Component Scoring</span>
                </div>
              </div>
              <button
                onClick={() => window.location.href = '/assessment-management'}
                className="ml-8 bg-white text-emerald-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-emerald-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 flex items-center gap-2"
              >
                Open Assessment Engine
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* NEW: Assessment Monitoring Dashboard */}
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl shadow-2xl p-8 mb-8 text-white hover:shadow-3xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                    <BarChart3 className="h-8 w-8" />
                  </div>
                  <h2 className="text-3xl font-extrabold" style={{ fontFamily: designSystem.typography.fonts.display }}>
                    Assessment Monitoring
                  </h2>
                </div>
                <p className="text-blue-50 text-lg mb-4">
                  Monitor all organization assessments, track phase progress (Qualitative → Payment → Quantitative → Evaluation), assign evaluators, and view certification tiers (Luminary, Inspired, Aspiring).
                </p>
                <div className="flex gap-4 text-sm">
                  <span className="bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm font-semibold">Phase Tracking</span>
                  <span className="bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm font-semibold">Certification Tiers</span>
                  <span className="bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm font-semibold">Evaluator Assignment</span>
                </div>
              </div>
              <button
                onClick={() => window.location.href = '/admin/assessment-monitoring'}
                className="ml-8 bg-white text-indigo-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 flex items-center gap-2"
              >
                Open Monitoring
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* NEW: User Management Card */}
          <div className="bg-gradient-to-r from-green-600 via-teal-600 to-cyan-600 rounded-2xl shadow-2xl p-8 mb-8 text-white hover:shadow-3xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                    <Users className="h-8 w-8" />
                  </div>
                  <h2 className="text-3xl font-extrabold" style={{ fontFamily: designSystem.typography.fonts.display }}>
                    User Management
                  </h2>
                </div>
                <p className="text-green-50 text-lg mb-4">
                  Manage organizations, approve registrations, create evaluators, assign permissions, and oversee all 6 personas (Organization Admin, Evaluator, Explorer, Mentor, Partner, Super Admin).
                </p>
                <div className="flex gap-4 text-sm">
                  <span className="bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm font-semibold">Approve Organizations</span>
                  <span className="bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm font-semibold">Manage Evaluators</span>
                  <span className="bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm font-semibold">User Roles</span>
                </div>
              </div>
              <button
                onClick={() => window.location.href = '/admin/user-management'}
                className="ml-8 bg-white text-green-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-green-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 flex items-center gap-2"
              >
                Open User Management
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* NEW: Best Practices Management Card */}
          <div className="bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 rounded-2xl shadow-2xl p-8 mb-8 text-white hover:shadow-3xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                    <BookOpen className="h-8 w-8" />
                  </div>
                  <h2 className="text-3xl font-extrabold" style={{ fontFamily: designSystem.typography.fonts.display }}>
                    Best Practices
                  </h2>
                </div>
                <p className="text-purple-50 text-lg mb-4">
                  Create and manage global best practices, review and approve organization submissions, respond to questions, and curate benchmark practices by pillar, size, and industry.
                </p>
                <div className="flex gap-4 text-sm">
                  <span className="bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm font-semibold">Global Practices</span>
                  <span className="bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm font-semibold">Org Submissions</span>
                  <span className="bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm font-semibold">Q&A Responses</span>
                </div>
              </div>
              <button
                onClick={() => window.location.href = '/admin/best-practices'}
                className="ml-8 bg-white text-purple-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-purple-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 flex items-center gap-2"
              >
                Open Best Practices
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Pillars Overview - Compact Grid */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: designSystem.typography.fonts.display }}>
              Pillars Overview
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pillars.map((pillar) => (
                <div key={pillar.pillar_id} data-testid={`admin-pillar-card-${pillar.pillar_number}`} className={`group bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-gray-200 hover:border-indigo-500 overflow-hidden transform hover:-translate-y-1 ${pillar.is_active === false ? 'opacity-60 grayscale' : ''}`}>
                  {/* Icon Section */}
                  <div className="h-32 bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center p-0 overflow-hidden">
                    {pillar.icon ? (
                      <img src={pillar.icon} alt={`${pillar.title} icon`} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">No Icon</div>
                    )}
                  </div>
                  
                  {/* Content Section */}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="inline-block px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-bold">
                        Pillar {pillar.pillar_number}
                      </span>
                      <span data-testid={`admin-pillar-status-${pillar.pillar_number}`} className={`px-2 py-1 rounded-full text-xs font-semibold ${pillar.is_active === false ? 'bg-amber-100 text-amber-800' : pillar.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {pillar.is_active === false ? 'inactive' : (pillar.status || 'draft')}
                      </span>
                    </div>
                    
                    <h3 className="text-sm font-bold text-gray-900 mb-2 line-clamp-2 leading-tight" style={{ fontFamily: designSystem.typography.fonts.display }}>
                      {pillar.title || pillar.name}
                    </h3>
                    
                    <div className="text-xs text-gray-600 mb-3">
                      {pillar.sub_pillars?.length || 0} modules
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handlePillarClick(pillar)}
                        className="flex-1 px-3 py-2 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
                      >
                        View
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingPillar(pillar);
                          setShowEditPillarModal(true);
                        }}
                        className="flex-1 px-3 py-2 bg-white border-2 border-indigo-600 text-indigo-600 text-xs rounded-lg hover:bg-indigo-50 transition-colors font-semibold"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Modals for Dashboard */}
      {showPillarPreview && selectedPillar && (
        <PillarPreviewModal
          pillar={selectedPillar}
          onClose={() => {
            setShowPillarPreview(false);
            setSelectedPillar(null);
          }}
        />
      )}

      {showEditPillarModal && editingPillar && (
        <EditPillarModal
          onClose={() => {
            setShowEditPillarModal(false);
            setEditingPillar(null);
          }}
          pillar={editingPillar}
          onSave={async (updatedPillar) => {
            try {
              const response = await fetch(`${BACKEND_URL}/api/pillars/admin/pillar/${updatedPillar.pillar_id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedPillar)
              });
              if (response.ok) {
                // CRITICAL FIX: AWAIT fetchData() to ensure UI refreshes before closing modal
                await fetchData();
                setShowEditPillarModal(false);
                setEditingPillar(null);
                alert('Pillar updated successfully!');
              } else {
                alert('Failed to update pillar - server error');
              }
            } catch (error) {
              console.error('Error updating pillar:', error);
              alert('Failed to update pillar');
            }
          }}
          onEditModule={handleEditModule}
          onToggleModuleStatus={handleToggleModuleStatus}
        />
      )}

      {editModuleModal}
      
      {showAddPillarModal && (
        <AddPillarModal
          onClose={() => setShowAddPillarModal(false)}
          onSave={async (newPillar) => {
            try {
              const response = await fetch(`${BACKEND_URL}/api/pillars`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newPillar)
              });
              if (response.ok) {
                fetchData();
                setShowAddPillarModal(false);
                alert('Pillar added successfully!');
              }
            } catch (error) {
              console.error('Error adding pillar:', error);
              alert('Failed to add pillar');
            }
          }}
        />
      )}
      </SystemAdminLayout>
    );
  }

  // CMS View - Show detailed pillar management
  if (activeView === 'cms') {
    return (
      <SystemAdminLayout>
        <div className="bg-gray-50 min-h-full">
          {/* Page Header */}
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-8 px-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-4xl font-extrabold mb-2" style={{ fontFamily: designSystem.typography.fonts.display }}>Content Management</h1>
                  <p className="text-lg text-indigo-100">Manage all pillar content, modules, and resources</p>
                </div>
                <button
                  onClick={() => setShowAddPillarModal(true)}
                  className="px-6 py-3 bg-white text-indigo-600 rounded-xl font-bold hover:bg-indigo-50 shadow-xl flex items-center space-x-2"
                >
                  <Plus className="h-5 w-5" />
                  <span>Add New Pillar</span>
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-8 py-8">
            {/* Navigation Tabs */}
            <div className="bg-white rounded-2xl shadow-xl mb-8 p-3 flex space-x-3 border border-gray-100">
              <button
                onClick={() => setActiveView('dashboard')}
                className="flex-1 px-6 py-3 rounded-xl hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 font-bold text-gray-700 transition-all duration-200 hover:shadow-md"
                style={{ fontSize: designSystem.typography.sizes.base, fontFamily: designSystem.typography.fonts.primary }}
              >
                Dashboard
              </button>
              <button
                onClick={() => setActiveView('cms')}
                className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold shadow-lg transform transition-all duration-200 hover:scale-105"
                style={{ fontSize: designSystem.typography.sizes.base, fontFamily: designSystem.typography.fonts.primary }}
              >
                Content Management
              </button>
              <button
                onClick={() => setActiveView('orgProgress')}
                className="flex-1 px-6 py-3 rounded-xl hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 font-bold text-gray-700 transition-all duration-200 hover:shadow-md"
                style={{ fontSize: designSystem.typography.sizes.base, fontFamily: designSystem.typography.fonts.primary }}
              >
                Organization Progress
              </button>
            </div>

            {/* Knowledge-Check Review Queue */}
            <QuizReviewQueue
              pillars={pillars}
              onEditModule={handleEditModule}
              reviewedBy={localStorage.getItem('cness_username') || 'system_admin'}
            />

            {/* Pillars List with Edit/View Actions */}
            <div className="space-y-4">
              {pillars.map((pillar) => (
                <div key={pillar.pillar_id} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6 border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      {/* Icon */}
                      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center p-2">
                        {pillar.icon ? (
                          <img src={pillar.icon} alt={pillar.title} className="w-12 h-12 object-contain" />
                        ) : (
                          <BookOpen className="w-8 h-8 text-white" />
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="inline-block px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-bold">
                            Pillar {pillar.pillar_number}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${pillar.is_active === false ? 'bg-amber-100 text-amber-800' : pillar.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                            {pillar.is_active === false ? 'inactive (not in CIS)' : (pillar.status || 'draft')}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2 break-words">{pillar.title || pillar.name}</h3>
                        <div className="text-sm text-gray-600">
                          {pillar.sub_pillars?.length || 0} modules • Last updated: {new Date(pillar.updated_at || pillar.created_at || Date.now()).toLocaleDateString()}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-3 ml-4">
                      <button
                        onClick={() => handlePillarClick(pillar)}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold flex items-center space-x-2"
                      >
                        <BookOpen className="h-4 w-4" />
                        <span>View</span>
                      </button>
                      <button
                        onClick={() => {
                          setEditingPillar(pillar);
                          setShowEditPillarModal(true);
                        }}
                        className="px-6 py-2 bg-white border-2 border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-semibold flex items-center space-x-2"
                      >
                        <Edit className="h-4 w-4" />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedPillar(pillar);
                          setShowSubPillarModal(true);
                        }}
                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold flex items-center space-x-2"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add Module</span>
                      </button>
                      <button
                        onClick={() => handleTogglePillarStatus(pillar)}
                        className={`px-6 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors ${
                          pillar.status === 'published'
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-yellow-600 text-white hover:bg-yellow-700'
                        }`}
                      >
                        {pillar.status === 'published' ? <CheckCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                        <span>{pillar.status === 'published' ? 'Published' : 'Draft'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Modals for CMS View */}
        {showPillarPreview && selectedPillar && (
          <PillarPreviewModal
            pillar={selectedPillar}
            onClose={() => {
              setShowPillarPreview(false);
              setSelectedPillar(null);
            }}
          />
        )}

        {showEditPillarModal && editingPillar && (
          <EditPillarModal
            onClose={() => {
              setShowEditPillarModal(false);
              setEditingPillar(null);
            }}
            pillar={editingPillar}
            onSave={async (updatedPillar) => {
              try {
                const response = await fetch(`${BACKEND_URL}/api/pillars/admin/pillar/${updatedPillar.pillar_id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(updatedPillar)
                });
                if (response.ok) {
                  // CRITICAL FIX: AWAIT fetchData() to ensure UI refreshes before closing modal
                  await fetchData();
                  setShowEditPillarModal(false);
                  setEditingPillar(null);
                  alert('Pillar updated successfully!');
                } else {
                  alert('Failed to update pillar - server error');
                }
              } catch (error) {
                console.error('Error updating pillar:', error);
                alert('Failed to update pillar');
              }
            }}
            onEditModule={handleEditModule}
            onToggleModuleStatus={handleToggleModuleStatus}
          />
        )}
        
        {showAddPillarModal && (
          <AddPillarModal
            onClose={() => setShowAddPillarModal(false)}
            onSave={async (newPillar) => {
              try {
                const response = await fetch(`${BACKEND_URL}/api/pillars`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(newPillar)
                });
                if (response.ok) {
                  fetchData();
                  setShowAddPillarModal(false);
                  alert('Pillar added successfully!');
                }
              } catch (error) {
                console.error('Error adding pillar:', error);
                alert('Failed to add pillar');
              }
            }}
          />
        )}

        {showSubPillarModal && selectedPillar && (
          <AddModuleModal
            onClose={() => setShowSubPillarModal(false)}
            pillar={selectedPillar}
            onSave={handleAddSubPillar}
          />
        )}

        {editModuleModal}
      </SystemAdminLayout>
    );
  }

  // Organization Progress View
  if (activeView === 'orgProgress') {
    return (
      <SystemAdminLayout>
      <div className="bg-gray-50 min-h-full p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Organization Progress Analytics</h1>
            <div className="space-y-4">
              {orgProgress.map((org) => (
                <div key={org._id} className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{org.organization_name}</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div><span className="text-gray-600">Total Users:</span> <span className="font-semibold">{org.total_users}</span></div>
                    <div><span className="text-gray-600">Avg Completion:</span> <span className="font-semibold">{Math.round(org.avg_completion || 0)}%</span></div>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => setActiveView('dashboard')}
              className="mt-6 px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>
      </SystemAdminLayout>
    );
  }

  return null;
};

const SubPillarModal = ({ onClose, onSave }) => {
  const contentRef = React.useRef(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(15);
  const [questions, setQuestions] = useState([]);
  const [resources, setResources] = useState([{ name: '', url: '', type: 'file' }]);
  
  const handleAddResource = () => {
    setResources([...resources, { name: '', url: '', type: 'file' }]);
  };
  
  const handleUpdateResource = (idx, field, value) => {
    const updated = [...resources];
    updated[idx][field] = value;
    setResources(updated);
  };
  
  const handleAddQuestion = () => {
    setQuestions([...questions, {
      question: '',
      options: ['', '', ''],
      correct_answer: 0
    }]);
  };
  
  const handleUpdateQuestion = (qIdx, field, value) => {
    const updated = [...questions];
    if (field === 'options') {
      updated[qIdx].options = value;
    } else {
      updated[qIdx][field] = value;
    }
    setQuestions(updated);
  };
  
  const handleDeleteQuestion = (qIdx) => {
    setQuestions(questions.filter((_, idx) => idx !== qIdx));
  };
  
  const handleSave = () => {
    if (!title.trim()) {
      alert('Module name is required');
      return;
    }
    if (!description.trim()) {
      alert('Description is required');
      return;
    }
    
    const content = contentRef.current?.innerHTML || '';
    if (!content.trim() || content === '<br>') {
      alert('Content is required');
      return;
    }
    
    onSave({
      title,
      description,
      icon,
      duration_minutes: durationMinutes,
      content,
      assessment_questions: questions.filter(q => q.question.trim()),
      resources: resources.filter(r => r.name && r.url),
      status: 'active',
      order: Date.now()
    });
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-6xl h-[90vh] flex flex-col shadow-2xl">
        <div className="flex-shrink-0 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-5 flex items-center justify-between rounded-t-xl">
          <h3 className="text-2xl font-bold">➕ Add New Module</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto bg-gray-50 p-8 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-base font-bold mb-2 text-red-600">Module Name * (Required)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 border-2 border-blue-300 rounded-lg focus:border-indigo-500 focus:outline-none"
              placeholder="e.g., Code of Ethics"
              required
            />
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-base font-bold mb-2">🎨 Module Icon (Optional)</label>
            <label className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer font-semibold inline-block">
              📁 Upload Icon Image
              <input 
                type="file" 
                accept="image/*" 
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setIcon(reader.result);
                    };
                    reader.readAsDataURL(file);
                  }
                }} 
                className="hidden" 
              />
            </label>
            {icon && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-semibold text-gray-700 mb-2">Preview:</p>
                <img src={icon} alt="Icon preview" className="w-20 h-20 object-contain border-2 border-gray-300 rounded-lg" />
              </div>
            )}
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-base font-bold mb-2 text-red-600">Description * (Required)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border-2 border-blue-300 rounded-lg focus:border-indigo-500 focus:outline-none"
              placeholder="Brief description of this module..."
              required
            />
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-base font-bold mb-2 text-red-600">Course Duration (minutes) * (Required)</label>
            <input
              type="number"
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(parseInt(e.target.value) || 15)}
              className="w-full px-4 py-3 border-2 border-blue-300 rounded-lg focus:border-indigo-500 focus:outline-none"
              min="1"
              required
            />
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-base font-bold mb-4 text-red-600">📚 Course Content * (Required)</label>
            <RichTextToolbar targetRef={contentRef} />
            <div
              ref={contentRef}
              contentEditable={true}
              className="prose prose-lg max-w-none border-2 border-indigo-400 p-6 rounded-lg min-h-[300px] bg-white focus:outline-none focus:border-indigo-600"
              style={{ lineHeight: '1.8' }}
            />
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <label className="block text-base font-bold">📎 Resources (Files & URLs)</label>
              <button onClick={handleAddResource} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                + Add Resource
              </button>
            </div>
            <div className="space-y-3">
              {resources.map((res, idx) => (
                <div key={idx} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="text"
                    value={res.name}
                    onChange={(e) => handleUpdateResource(idx, 'name', e.target.value)}
                    placeholder="Resource name"
                    className="flex-1 px-3 py-2 border rounded"
                  />
                  <input
                    type="text"
                    value={res.url}
                    onChange={(e) => handleUpdateResource(idx, 'url', e.target.value)}
                    placeholder="File path or URL"
                    className="flex-1 px-3 py-2 border rounded"
                  />
                  <button
                    onClick={() => setResources(resources.filter((_, i) => i !== idx))}
                    className="p-2 text-red-600 hover:bg-red-100 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <label className="block text-base font-bold">📝 Assessment Questions (Optional)</label>
              <button onClick={handleAddQuestion} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold">
                + Add Question
              </button>
            </div>
            {questions.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No questions yet. Click "Add Question" to create assessment questions.</p>
            ) : (
              <div className="space-y-4">
                {questions.map((q, qIdx) => (
                  <div key={qIdx} className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-bold text-gray-700">Question {qIdx + 1}</span>
                      <button
                        onClick={() => handleDeleteQuestion(qIdx)}
                        className="text-red-600 hover:text-red-800 p-2"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                    <input
                      type="text"
                      value={q.question}
                      onChange={(e) => handleUpdateQuestion(qIdx, 'question', e.target.value)}
                      placeholder="Enter question text..."
                      className="w-full px-3 py-2 border-2 border-green-400 rounded-lg mb-3 font-semibold focus:border-green-600 focus:outline-none"
                    />
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600 mb-2">Select the correct answer:</p>
                      {q.options.map((opt, optIdx) => (
                        <div key={optIdx} className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name={`question-${qIdx}`}
                            checked={q.correct_answer === optIdx}
                            onChange={() => handleUpdateQuestion(qIdx, 'correct_answer', optIdx)}
                            className="h-5 w-5 text-green-600"
                          />
                          <span className="font-bold text-gray-700">{String.fromCharCode(65 + optIdx)}.</span>
                          <input
                            type="text"
                            value={opt}
                            onChange={(e) => {
                              const newOptions = [...q.options];
                              newOptions[optIdx] = e.target.value;
                              handleUpdateQuestion(qIdx, 'options', newOptions);
                            }}
                            placeholder={`Option ${String.fromCharCode(65 + optIdx)}`}
                            className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex-shrink-0 bg-gray-50 px-8 py-4 border-t flex justify-end space-x-4">
          <button onClick={onClose} className="px-8 py-3 border-2 border-gray-300 rounded-xl hover:bg-white font-semibold">Cancel</button>
          <button onClick={handleSave} className="px-8 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 font-bold">💾 Add Module</button>
        </div>
      </div>
    </div>
  );
};

const EditModuleModal = ({ onClose, pillar, module, onSave }) => {
  const [title, setTitle] = React.useState(module?.title || '');
  const [description, setDescription] = React.useState(module?.description || '');
  const [icon, setIcon] = React.useState(module?.icon || '');
  const [content, setContent] = React.useState(module?.content || '');
  const [status, setStatus] = React.useState(module?.status || 'active');
  const [questions, setQuestions] = React.useState(module?.assessment_questions || []);
  const [generating, setGenerating] = React.useState(false);
  const [approving, setApproving] = React.useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const contentRef = React.useRef(null);
  const descriptionRef = React.useRef(null);
  
  React.useEffect(() => {
    if (contentRef.current && content) {
      contentRef.current.innerHTML = content;
    }
    if (descriptionRef.current && description) {
      descriptionRef.current.innerHTML = description;
    }
  }, [content, description]);

  const handleSave = () => {
    const contentHTML = contentRef.current ? contentRef.current.innerHTML : content;
    const descriptionHTML = descriptionRef.current ? descriptionRef.current.innerHTML : description;
    
    // CRITICAL FIX: Always preserve the original sub_pillar_id from the module
    // Never use title as identifier - it can change during editing
    // If no sub_pillar_id exists (legacy), use the ORIGINAL title (not the edited one)
    const moduleId = module.sub_pillar_id || module.id || module.title || 'unknown';
    
    onSave({
      sub_pillar_id: moduleId,
      original_title: module.title, // Keep track of original title for backend lookup
      title,
      description: descriptionHTML,
      icon,
      content: contentHTML,
      status,
      assessment_questions: questions
    });
  };

  const handleAddQuestion = () => {
    setQuestions([...questions, {
      question: '',
      options: ['', '', ''],
      correct_answer: 0
    }]);
  };

  const handleUpdateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const handleDeleteQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleApproveQuiz = async () => {
    if (!module?.sub_pillar_id) return;
    setApproving(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/pillars/admin/quiz-approve/${pillar.pillar_id}/${encodeURIComponent(module.sub_pillar_id)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reviewed_by: localStorage.getItem('cness_username') || 'system_admin' })
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Approval failed');
      setQuestions(questions.map((q) => ({ ...q, review_status: 'approved' })));
      alert('Knowledge-check questions approved.');
    } catch (error) {
      alert(`Could not approve: ${error.message}`);
    } finally {
      setApproving(false);
    }
  };

  const handleGenerateQuiz = async () => {
    if (!module?.sub_pillar_id) {
      alert('This module has no ID yet — save it once before generating a quiz.');
      return;
    }
    setGenerating(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/pillars/admin/generate-quiz/${pillar.pillar_id}/${encodeURIComponent(module.sub_pillar_id)}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Generation failed');
      setQuestions(data.questions);
    } catch (error) {
      alert(`Could not generate questions: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="flex-shrink-0 bg-gradient-to-r from-green-600 to-teal-600 text-white px-8 py-5 flex items-center justify-between rounded-t-xl">
          <h3 className="text-2xl font-bold">✏️ Edit Module: {module?.title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="h-7 w-7" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-8 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Module Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
              placeholder="e.g., Code of Ethics"
            />
          </div>

          {/* Status Toggle */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Status</label>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setStatus('active')}
                className={`px-6 py-2 rounded-lg font-semibold ${
                  status === 'active' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setStatus('inactive')}
                className={`px-6 py-2 rounded-lg font-semibold ${
                  status === 'inactive' 
                    ? 'bg-red-600 text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                Inactive
              </button>
            </div>
          </div>

          {/* Icon Upload */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">🎨 Module Icon (Optional)</label>
            <label className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer font-semibold inline-block">
              📁 Upload Icon Image
              <input 
                type="file" 
                accept="image/*" 
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setIcon(reader.result);
                    };
                    reader.readAsDataURL(file);
                  }
                }} 
                className="hidden" 
              />
            </label>
            {icon && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-semibold text-gray-700 mb-2">Preview:</p>
                <img src={icon} alt="Icon preview" className="w-20 h-20 object-contain border-2 border-gray-300 rounded-lg" />
              </div>
            )}
          </div>

          {/* Description with Rich Text Editor */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Short Description</label>
            <RichTextToolbar targetRef={descriptionRef} />
            <div
              ref={descriptionRef}
              contentEditable
              className="w-full min-h-[120px] px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none bg-white"
              style={{ fontFamily: designSystem.typography.fonts.body }}
              suppressContentEditableWarning
            />
          </div>

          {/* Content Editor */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Module Content (HTML)</label>
            <RichTextToolbar targetRef={contentRef} />
            <div
              ref={contentRef}
              contentEditable
              className="w-full min-h-[300px] px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none prose max-w-none"
              style={{ whiteSpace: 'pre-wrap' }}
            />
          </div>

          {/* Assessment Questions */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <label className="text-sm font-bold text-gray-700">Knowledge-Check Questions</label>
                <p className="text-xs text-gray-500 mt-1">Skippable for learners · never affects the CIS score</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  data-testid="approve-quiz-btn"
                  onClick={handleApproveQuiz}
                  disabled={approving || questions.length === 0}
                  className={`px-4 py-2 rounded-lg font-semibold text-white transition-colors ${approving || questions.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {approving ? 'Approving…' : '✓ Approve questions'}
                </button>
                <button
                  data-testid="generate-quiz-ai-btn"
                  onClick={handleGenerateQuiz}
                  disabled={generating}
                  className={`px-4 py-2 rounded-lg font-semibold text-white transition-colors ${generating ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                >
                  {generating ? 'Generating…' : '✨ Generate with AI'}
                </button>
                <button
                  onClick={handleAddQuestion}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                >
                  + Add Question
                </button>
              </div>
            </div>
            
            {questions.map((q, qIndex) => (
              <div key={qIndex} className="p-4 bg-gray-50 rounded-lg border-2 border-gray-200 mb-4">
                <div className="flex items-start justify-between mb-3">
                  <span className="font-bold text-gray-700 flex items-center gap-2">
                    Question {qIndex + 1}
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${q.review_status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                      {q.review_status === 'approved' ? 'approved' : 'AI draft'}
                    </span>
                  </span>
                  <button
                    onClick={() => handleDeleteQuestion(qIndex)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
                
                <input
                  type="text"
                  value={q.question}
                  onChange={(e) => handleUpdateQuestion(qIndex, 'question', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg mb-3"
                  placeholder="Enter question text..."
                />
                
                <div className="space-y-2">
                  {q.options.map((opt, optIndex) => (
                    <div key={optIndex} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name={`correct-${qIndex}`}
                        checked={q.correct_answer === optIndex}
                        onChange={() => handleUpdateQuestion(qIndex, 'correct_answer', optIndex)}
                        className="w-4 h-4"
                      />
                      <input
                        type="text"
                        value={opt}
                        onChange={(e) => {
                          const newOptions = [...q.options];
                          newOptions[optIndex] = e.target.value;
                          handleUpdateQuestion(qIndex, 'options', newOptions);
                        }}
                        className="flex-1 px-3 py-2 border rounded-lg"
                        placeholder={`Option ${optIndex + 1}...`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex-shrink-0 bg-gray-50 px-8 py-4 border-t flex justify-end space-x-4 rounded-b-xl">
          <button onClick={onClose} className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 font-bold">
            Cancel
          </button>
          <button onClick={handleSave} className="px-8 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 font-bold">
            Save Module
          </button>
        </div>
      </div>
    </div>
  );
};

// Pillar Preview Modal (Read-Only View)
const PillarPreviewModal = ({ pillar, onClose }) => {
  if (!pillar) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-6xl h-[90vh] flex flex-col shadow-2xl">
        <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white px-8 py-5 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center space-x-4">
            <h3 className="text-2xl font-bold">📖 Pillar {pillar.pillar_number}: {pillar.title || pillar.name}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="h-7 w-7" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto bg-gray-50 p-8 space-y-6">
          {/* Icon and Title */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center space-x-6">
              <div className="flex-shrink-0 w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center p-4">
                {pillar.icon ? (
                  <img src={pillar.icon} alt={pillar.title} className="w-full h-full object-contain" />
                ) : (
                  <BookOpen className="w-12 h-12 text-white" />
                )}
              </div>
              <div className="flex-1">
                <div className="inline-block px-4 py-2 bg-indigo-100 text-indigo-800 rounded-full text-sm font-bold mb-2">
                  Pillar {pillar.pillar_number}
                </div>
                <h2 className="text-3xl font-bold text-gray-900">{pillar.title || pillar.name}</h2>
                <div className="mt-3 flex items-center space-x-4 text-sm text-gray-600">
                  <span className={`px-3 py-1 rounded-full font-semibold ${pillar.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {pillar.status || 'draft'}
                  </span>
                  <span>{pillar.sub_pillars?.length || 0} Modules</span>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">📋 Description</h3>
            <div 
              className="prose prose-lg max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: pillar.description || '<p class="text-gray-500">No description available</p>' }}
            />
          </div>

          {/* Modules */}
          {pillar.sub_pillars && pillar.sub_pillars.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">📚 Learning Modules ({pillar.sub_pillars.length})</h3>
              <div className="space-y-3">
                {pillar.sub_pillars.map((module, idx) => (
                  <div key={module.sub_pillar_id || idx} className="p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-bold text-lg text-gray-900 mb-2">{module.title}</h4>
                        <div 
                          className="text-sm text-gray-600 line-clamp-2"
                          dangerouslySetInnerHTML={{ __html: module.description || '' }}
                        />
                        <div className="text-xs text-gray-500 mt-2">
                          {module.assessment_questions?.length || 0} assessment questions
                        </div>
                      </div>
                      <div className="flex items-center ml-4">
                        <span className={`px-4 py-2 rounded-lg font-semibold text-sm ${
                            module.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-200 text-gray-600'
                          }`}>
                          {module.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resources */}
          {pillar.resources && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">📎 Resources</h3>
              
              {pillar.resources.case_studies && pillar.resources.case_studies.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-700 mb-2">📚 Case Studies</h4>
                  <ul className="space-y-1">
                    {pillar.resources.case_studies.map((cs, idx) => (
                      <li key={idx} className="text-sm text-blue-600">• {cs.name}</li>
                    ))}
                  </ul>
                </div>
              )}

              {pillar.resources.reading_material && pillar.resources.reading_material.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-700 mb-2">📖 Reading Material</h4>
                  <ul className="space-y-1">
                    {pillar.resources.reading_material.map((rm, idx) => (
                      <li key={idx} className="text-sm text-blue-600">• {rm.name}</li>
                    ))}
                  </ul>
                </div>
              )}

              {pillar.resources.video_links && pillar.resources.video_links.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-700 mb-2">🎥 Video Links</h4>
                  <ul className="space-y-1">
                    {pillar.resources.video_links.map((vl, idx) => (
                      <li key={idx} className="text-sm text-blue-600">• {vl.name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="flex-shrink-0 bg-gray-50 px-8 py-4 border-t flex justify-end rounded-b-xl">
          <button onClick={onClose} className="px-8 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-bold text-lg">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit Pillar Modal
const EditPillarModal = ({ onClose, pillar, onSave, onEditModule, onToggleModuleStatus }) => {
  const nameRef = React.useRef(null);
  const descriptionRef = React.useRef(null);
  const [icon, setIcon] = React.useState(pillar?.icon || '');
  const [status, setStatus] = React.useState(pillar?.status || 'draft');
  
  React.useEffect(() => {
    // Set pillar name as TEXT ONLY (no HTML rendering)
    if (nameRef.current && pillar?.name) {
      // CRITICAL FIX: Always set name as plain text to prevent MS Office markup issues
      nameRef.current.textContent = pillar.name;
    }
    
    // Set pillar description with HTML formatting
    if (descriptionRef.current && pillar?.description) {
      // Clean Microsoft Office XML markup and set clean HTML content
      const cleanedHTML = cleanMicrosoftHTML(pillar.description);
      descriptionRef.current.innerHTML = cleanedHTML;
    }
  }, [pillar, nameRef, descriptionRef]);

  const handleSave = () => {
    let name = nameRef.current?.innerHTML || '';
    let description = descriptionRef.current?.innerHTML || '';
    
    // CRITICAL FIX: Clean MS Office markup from name
    // Strip all HTML tags from name and get clean text only
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = name;
    name = tempDiv.textContent || tempDiv.innerText || '';
    
    // Clean description using the existing cleanMicrosoftHTML function
    description = cleanMicrosoftHTML(description);
    
    onSave({
      ...pillar,
      name,
      title: name,  // CRITICAL FIX: Update both name and title fields for UI refresh
      icon,
      description,
      status
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-6xl h-[90vh] flex flex-col shadow-2xl">
        <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white px-8 py-6 flex items-center justify-between rounded-t-2xl">
          <h3 className="text-3xl font-bold">✏️ Edit Pillar {pillar?.pillar_number}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-all">
            <X className="h-7 w-7" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto bg-gray-50 p-8 space-y-6">
          {/* Pillar Name */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <label className="block text-base font-bold text-gray-900 mb-3">📝 Pillar Name (Supports Rich Formatting)</label>
            <RichTextToolbar targetRef={nameRef} />
            <div
              ref={nameRef}
              contentEditable
              className="w-full min-h-[60px] px-4 py-3 border-2 border-indigo-300 rounded-lg focus:border-indigo-600 focus:outline-none text-lg bg-white"
              style={{ lineHeight: '1.5' }}
              placeholder="e.g., Conscious Leadership"
            />
          </div>

          {/* Icon Upload */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <label className="block text-base font-bold text-gray-900 mb-3">🎨 Pillar Icon</label>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <label className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 cursor-pointer font-semibold">
                  📁 Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setIcon(reader.result);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="hidden"
                  />
                </label>
                <span className="text-sm text-gray-600">or paste Base64/URL below</span>
              </div>
              <input
                type="text"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                className="w-full px-4 py-3 border-2 border-indigo-300 rounded-lg focus:border-indigo-600 focus:outline-none"
                placeholder="Paste base64 or image URL"
              />
              {icon && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Preview:</p>
                  <img src={icon} alt="Icon preview" className="w-20 h-20 object-contain border-2 border-gray-300 rounded-lg" onError={(e) => e.target.style.display = 'none'} />
                </div>
              )}
            </div>
          </div>

          {/* Status */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <label className="block text-base font-bold text-gray-900 mb-3">📊 Publication Status</label>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setStatus('draft')}
                className={`px-8 py-3 rounded-xl font-bold transition-all ${
                  status === 'draft'
                    ? 'bg-yellow-500 text-white shadow-lg scale-105'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                📝 Draft
              </button>
              <button
                onClick={() => setStatus('published')}
                className={`px-8 py-3 rounded-xl font-bold transition-all ${
                  status === 'published'
                    ? 'bg-green-500 text-white shadow-lg scale-105'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                ✅ Published
              </button>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <label className="block text-base font-bold text-gray-900 mb-3">📄 Pillar Description</label>
            <RichTextToolbar targetRef={descriptionRef} />
            <div
              ref={descriptionRef}
              contentEditable
              className="w-full min-h-[400px] px-6 py-4 border-2 border-indigo-300 rounded-lg focus:border-indigo-600 focus:outline-none prose prose-lg max-w-none bg-white"
              style={{ lineHeight: '1.8' }}
            />
          </div>

          {/* Modules Section */}
          {pillar?.sub_pillars && pillar.sub_pillars.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">📚 Modules ({pillar.sub_pillars.length})</h4>
              <div className="space-y-3">
                {pillar.sub_pillars.map((module, idx) => (
                  <div key={module.sub_pillar_id || idx} className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
                    <div className="flex-1">
                      <h5 className="font-bold text-gray-900">{module.title}</h5>
                      <p className="text-sm text-gray-600">{module.assessment_questions?.length || 0} questions</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleModuleStatus(pillar.pillar_id, module.sub_pillar_id, module.status);
                        }}
                        className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                          module.status === 'active'
                            ? 'bg-green-500 text-white hover:bg-green-600'
                            : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                        }`}
                      >
                        {module.status === 'active' ? '✓ Active' : '✗ Inactive'}
                      </button>
                      <button
                        data-testid={`edit-module-btn-${module.sub_pillar_id || module.title}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onClose();
                          onEditModule(pillar, module);
                        }}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold text-sm"
                      >
                        ✏️ Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="flex-shrink-0 bg-gray-100 px-8 py-5 border-t-2 border-gray-200 flex justify-end space-x-4 rounded-b-2xl">
          <button onClick={onClose} className="px-8 py-3 bg-white border-2 border-gray-400 text-gray-700 rounded-xl hover:bg-gray-50 font-bold text-lg shadow">
            Cancel
          </button>
          <button onClick={handleSave} className="px-10 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 font-bold text-lg shadow-xl">
            💾 Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

const AddPillarModal = ({ onClose, onSave }) => {
  const descriptionRef = React.useRef(null);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('');
  const [pillarNumber, setPillarNumber] = useState(1);
  const [caseStudies, setCaseStudies] = useState([{ name: '', url: '' }]);
  const [readingMaterial, setReadingMaterial] = useState([{ name: '', url: '' }]);
  const [videoUploads, setVideoUploads] = useState([{ name: '', url: '' }]);
  const [videoLinks, setVideoLinks] = useState([{ name: '', url: '' }]);
  
  const handleSave = () => {
    const description = descriptionRef.current?.innerHTML || '';
    onSave({
      pillar_id: `pillar_${Date.now()}`,
      pillar_number: pillarNumber,
      name,
      icon,
      description,
      status: 'active',
      sub_pillars: [],
      total_duration_minutes: 0,
      resources: {
        case_studies: caseStudies.filter(r => r.name || r.url),
        reading_material: readingMaterial.filter(r => r.name || r.url),
        video_uploads: videoUploads.filter(r => r.name || r.url),
        video_links: videoLinks.filter(r => r.name || r.url)
      }
    });
  };
  
  const addResource = (type) => {
    if (type === 'case') setCaseStudies([...caseStudies, { name: '', url: '' }]);
    if (type === 'reading') setReadingMaterial([...readingMaterial, { name: '', url: '' }]);
    if (type === 'video_upload') setVideoUploads([...videoUploads, { name: '', url: '' }]);
    if (type === 'video_link') setVideoLinks([...videoLinks, { name: '', url: '' }]);
  };
  
  const updateResource = (type, idx, field, value) => {
    if (type === 'case') {
      const updated = [...caseStudies];
      updated[idx][field] = value;
      setCaseStudies(updated);
    }
    if (type === 'reading') {
      const updated = [...readingMaterial];
      updated[idx][field] = value;
      setReadingMaterial(updated);
    }
    if (type === 'video_upload') {
      const updated = [...videoUploads];
      updated[idx][field] = value;
      setVideoUploads(updated);
    }
    if (type === 'video_link') {
      const updated = [...videoLinks];
      updated[idx][field] = value;
      setVideoLinks(updated);
    }
  };
  
  const deleteResource = (type, idx) => {
    if (type === 'case') setCaseStudies(caseStudies.filter((_, i) => i !== idx));
    if (type === 'reading') setReadingMaterial(readingMaterial.filter((_, i) => i !== idx));
    if (type === 'video_upload') setVideoUploads(videoUploads.filter((_, i) => i !== idx));
    if (type === 'video_link') setVideoLinks(videoLinks.filter((_, i) => i !== idx));
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-6xl h-[90vh] flex flex-col shadow-2xl">
        <div className="flex-shrink-0 bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white px-8 py-5 flex items-center justify-between rounded-t-xl">
          <h3 className="text-2xl font-bold">➕ Add New Pillar</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="h-7 w-7" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto bg-gray-50 p-8 space-y-8">
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-base font-bold mb-2">Pillar Number</label>
              <input type="number" value={pillarNumber} onChange={(e) => setPillarNumber(parseInt(e.target.value))} className="w-full px-4 py-3 border-2 border-purple-300 rounded-lg" min="1" />
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-base font-bold mb-2">🎨 Pillar Icon</label>
              <label className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer font-semibold inline-block">
                📁 Upload Icon Image
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setIcon(reader.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }} 
                  className="hidden" 
                />
              </label>
              {icon && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Preview:</p>
                  <img src={icon} alt="Icon preview" className="w-20 h-20 object-contain border-2 border-gray-300 rounded-lg" />
                </div>
              )}
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-base font-bold mb-2">Pillar Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-3 border-2 border-purple-300 rounded-lg" placeholder="e.g., Conscious Leadership" />
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-base font-bold mb-4">📋 Description</label>
            <RichTextToolbar />
            <div ref={descriptionRef} contentEditable={true} suppressContentEditableWarning={true} className="prose prose-lg max-w-none border-2 border-purple-300 p-6 rounded-lg min-h-[200px] bg-purple-50 focus:outline-none" style={{ lineHeight: '1.8' }} />
          </div>
          
          {/* Case Studies */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-gray-900">📚 Case Studies</h4>
              <button onClick={() => addResource('case')} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">+ Add</button>
            </div>
            <div className="space-y-3">
              {caseStudies.map((r, idx) => (
                <div key={idx} className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
                  <input type="text" value={r.name} onChange={(e) => updateResource('case', idx, 'name', e.target.value)} placeholder="Case study name" className="flex-1 px-3 py-2 border rounded" />
                  <input type="text" value={r.url} onChange={(e) => updateResource('case', idx, 'url', e.target.value)} placeholder="File path or URL" className="flex-1 px-3 py-2 border rounded" />
                  <button onClick={() => deleteResource('case', idx)} className="p-2 text-red-600 hover:bg-red-100 rounded"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
          
          {/* Reading Material */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-gray-900">📖 Reading Material</h4>
              <button onClick={() => addResource('reading')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add</button>
            </div>
            <div className="space-y-3">
              {readingMaterial.map((r, idx) => (
                <div key={idx} className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                  <input type="text" value={r.name} onChange={(e) => updateResource('reading', idx, 'name', e.target.value)} placeholder="Reading material name" className="flex-1 px-3 py-2 border rounded" />
                  <input type="text" value={r.url} onChange={(e) => updateResource('reading', idx, 'url', e.target.value)} placeholder="File path or URL" className="flex-1 px-3 py-2 border rounded" />
                  <button onClick={() => deleteResource('reading', idx)} className="p-2 text-blue-600 hover:bg-blue-100 rounded"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
          
          {/* Video Uploads */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-gray-900">🎥 Video Uploads</h4>
              <button onClick={() => addResource('video_upload')} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">+ Add</button>
            </div>
            <div className="space-y-3">
              {videoUploads.map((r, idx) => (
                <div key={idx} className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
                  <input type="text" value={r.name} onChange={(e) => updateResource('video_upload', idx, 'name', e.target.value)} placeholder="Video name" className="flex-1 px-3 py-2 border rounded" />
                  <input type="text" value={r.url} onChange={(e) => updateResource('video_upload', idx, 'url', e.target.value)} placeholder="Video file path" className="flex-1 px-3 py-2 border rounded" />
                  <button onClick={() => deleteResource('video_upload', idx)} className="p-2 text-purple-600 hover:bg-purple-100 rounded"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
          
          {/* Video Links */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-gray-900">🔗 Video Links (YouTube, Vimeo)</h4>
              <button onClick={() => addResource('video_link')} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">+ Add</button>
            </div>
            <div className="space-y-3">
              {videoLinks.map((r, idx) => (
                <div key={idx} className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                  <input type="text" value={r.name} onChange={(e) => updateResource('video_link', idx, 'name', e.target.value)} placeholder="Video title" className="flex-1 px-3 py-2 border rounded" />
                  <input type="url" value={r.url} onChange={(e) => updateResource('video_link', idx, 'url', e.target.value)} placeholder="Video URL" className="flex-1 px-3 py-2 border rounded" />
                  <button onClick={() => deleteResource('video_link', idx)} className="p-2 text-green-600 hover:bg-green-100 rounded"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="flex-shrink-0 bg-gray-50 px-8 py-4 border-t flex justify-end space-x-4 rounded-b-xl">
          <button onClick={onClose} className="px-8 py-3 border-2 border-gray-300 rounded-xl hover:bg-white font-semibold">Cancel</button>
          <button onClick={handleSave} className="px-8 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 font-bold text-lg">💾 Save Pillar</button>
        </div>
      </div>
    </div>
  );
};

// Alias for SubPillarModal
const AddModuleModal = SubPillarModal;

export default SystemAdminDashboard;
